'use client'

import { useEffect, useRef, useState } from 'react'
import {
  loadYoutubeIframeApi,
  type YTPlayer,
} from '@/lib/load-youtube-iframe-api'

const LIVE_TV_VOLUME = 50

function applyLiveAudio(player: YTPlayer) {
  try {
    player.setVolume(LIVE_TV_VOLUME)
    player.unMute()
  } catch {
    /* ignore */
  }
}

/**
 * Canlı yayın oynatıcı.
 * Önce sessiz autoplay (tarayıcı engeli yok), ardından ses %50.
 */
export default function LiveTvPlayer({
  videoId,
  title,
}: {
  videoId: string
  title: string
}) {
  const hostRef = useRef<HTMLDivElement>(null)
  const playerRef = useRef<YTPlayer | null>(null)
  const [error, setError] = useState(false)

  useEffect(() => {
    let cancelled = false
    const host = hostRef.current
    if (!host) return

    setError(false)
    host.replaceChildren()
    const mount = document.createElement('div')
    mount.className = 'h-full w-full'
    host.appendChild(mount)

    void loadYoutubeIframeApi()
      .then((YT) => {
        if (cancelled || !mount.isConnected) return

        const player = new YT.Player(mount, {
          videoId,
          width: '100%',
          height: '100%',
          playerVars: {
            autoplay: 1,
            // Sessiz başlat → güvenilir autoplay; onReady’de %50’ye açılır
            mute: 1,
            rel: 0,
            modestbranding: 1,
            playsinline: 1,
            controls: 1,
            fs: 1,
            origin: window.location.origin,
          },
          events: {
            onReady: (event) => {
              if (cancelled) return
              applyLiveAudio(event.target)
              try {
                event.target.playVideo()
              } catch {
                /* ignore */
              }
            },
            onStateChange: (event) => {
              if (cancelled) return
              if (event.data === YT.PlayerState.PLAYING) {
                // YouTube bazen PLAYING’de mute’u sıfırlar
                applyLiveAudio(event.target)
              }
            },
            onError: () => {
              if (!cancelled) setError(true)
            },
          },
        })

        playerRef.current = player
      })
      .catch(() => {
        if (!cancelled) setError(true)
      })

    return () => {
      cancelled = true
      try {
        playerRef.current?.destroy()
      } catch {
        /* ignore */
      }
      playerRef.current = null
      host.replaceChildren()
    }
  }, [videoId])

  return (
    <div className="absolute inset-0 h-full w-full" title={title} aria-label={title}>
      <div
        ref={hostRef}
        className={`absolute inset-0 h-full w-full [&_iframe]:absolute [&_iframe]:inset-0 [&_iframe]:h-full [&_iframe]:w-full ${
          error ? 'invisible' : ''
        }`}
      />
      {error ? (
        <div className="absolute inset-0 flex items-center justify-center px-4 text-center">
          <p className="text-sm text-white/80">Yayın yüklenemedi. Sayfayı yenileyin.</p>
        </div>
      ) : null}
    </div>
  )
}
